import { atom, useRecoilState } from 'recoil';
import * as z from 'zod';

import { colors } from '@gitbook/react-uikit';
import { localStorage } from '@gitbook/util-local-storage';

/**
 * This hook allows us to read & persist recently used
 * colors on the ColorPicker. Peristence happens on LocalStorage/AsyncStorage
 */
export function useRecentColors() {
    const [recentColors, setRecentColors] = useRecoilState(colorPickerRecentColorsAtom);

    const addRecentColor = (color: string) => {
        setRecentColors((prevColors) => {
            const newColors = prevColors.filter((c) => c !== color);
            return [color, ...newColors].slice(0, RECENT_COLORS_MAX_LENGTH);
        });
    };

    return { recentColors: sanitizeColors(recentColors), addRecentColor };
}

const cssColorSchema = z
    .string()
    .length(7)
    .regex(/^#/)
    .transform((color) => color.toLowerCase());

/**
 * Sanitize the colors array to ensure it's always a unique, static lengthed, array of strings
 */
const sanitizeColors = (colors: string[]): string[] => {
    const uniqueColors = new Set(
        colors.reduce<string[]>((validColors, color) => {
            const parsed = cssColorSchema.safeParse(color);
            if (parsed.success) {
                validColors.push(parsed.data);
            }
            return validColors;
        }, [])
    );

    const remainingDefaults = DEFAULT_COLOR_SUGGESTIONS.filter(
        (color) => !uniqueColors.has(color.toLowerCase())
    );

    return [...uniqueColors, ...remainingDefaults].slice(0, RECENT_COLORS_MAX_LENGTH);
};

/**
 * Default color suggestions for the ColorPicker
 */
const DEFAULT_COLOR_SUGGESTIONS = [
    colors.primary.light,
    colors.violet.light,
    colors.fuchsia.light,
    colors.scarlet.light,
    colors.orange.light,
    colors.green.light,
];

const RECENT_COLORS_MAX_LENGTH = DEFAULT_COLOR_SUGGESTIONS.length;

/**
 * Recoil based state for recently used colors that is persisted
 * on the (Local/Async)Storage
 */
const colorPickerRecentColorsAtom = atom<Array<string>>({
    key: 'colorPickerRecentColorsAtom',
    default: DEFAULT_COLOR_SUGGESTIONS,
    effects: [
        localStorage.recoilEffect('@recoil/colorPickerRecentColorsAtom', DEFAULT_COLOR_SUGGESTIONS),
    ],
});
